import {equals} from 'ramda';

import {Nullish} from 'shared/web';

import {emailHtmlToPlainText} from './emailHtmlToPlainText';

interface MessageContent {
  subject?: string | Nullish;
  body?: string | Nullish;
  /** Files attached to the message; an added or removed file is work worth confirming. */
  attachmentCount?: number;
}

const toText = (body: string | Nullish, isHtml: boolean) =>
  (isHtml ? emailHtmlToPlainText(body ?? '') : (body ?? '')).trim();

/** Images and link targets, which change the message without changing its visible text. */
const toReferences = (body: string | Nullish) => {
  const document = new DOMParser().parseFromString(body ?? '', 'text/html');
  return [
    ...Array.from(document.querySelectorAll('img')).map((image) => image.getAttribute('src')),
    ...Array.from(document.querySelectorAll('a')).map((link) => link.getAttribute('href')),
  ];
};

/**
 * Compares what the employee reads and what the message carries, not the markup: the editor
 * rewrites the HTML it was given (`<p><br></p>` for an empty line), and that alone is not a change
 * worth confirming. Bold or italic on unchanged text is not counted either.
 */
export const hasMessageChanged = (
  current: MessageContent,
  initial: MessageContent,
  isHtml: boolean
): boolean =>
  (current.subject ?? '').trim() !== (initial.subject ?? '').trim() ||
  toText(current.body, isHtml) !== toText(initial.body, isHtml) ||
  (isHtml && !equals(toReferences(current.body), toReferences(initial.body))) ||
  (current.attachmentCount ?? 0) !== (initial.attachmentCount ?? 0);
